
import React from 'react';
import { CareerLevel } from '../../../types/game';
import { useGame } from '../../../contexts/GameContext';
import { Progress } from '@/components/ui/progress';
import { CheckCircle, ChevronRight, Award, Clock } from 'lucide-react';

interface CareerTabProps {
  experience: number;
  careerLevel: CareerLevel;
}

const CareerTab: React.FC<CareerTabProps> = ({ experience, careerLevel }) => {
  const { selectedRole, yearsPassed } = useGame();
  
  if (!selectedRole) return <div>No career path selected</div>;

  // Find current level information
  const currentLevelPath = selectedRole.careerPath.find(path => path.level === careerLevel);
  const currentLevelIndex = selectedRole.careerPath.findIndex(path => path.level === careerLevel);
  
  // Find next level information (if exists)
  const nextLevelPath = currentLevelIndex < selectedRole.careerPath.length - 1 
    ? selectedRole.careerPath[currentLevelIndex + 1] 
    : null;
  
  // Calculate experience progress
  const expRequired = nextLevelPath ? nextLevelPath.requiredExperience : 1000;
  const expProgress = Math.min(100, (experience / expRequired) * 100);
  
  // Calculate years progress
  const yearsRequired = nextLevelPath ? nextLevelPath.requiredYears : 0;
  const yearsProgress = yearsRequired > 0 ? Math.min(100, (yearsPassed / yearsRequired) * 100) : 100;
  
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-white mb-4">Career Path Progress</h3>
        <div className="space-y-8">
          {selectedRole.careerPath.map((path, index) => {
            const isCurrentLevel = path.level === careerLevel;
            const isPastLevel = currentLevelIndex > index;
            const isFutureLevel = currentLevelIndex < index;
            
            return (
              <div 
                key={path.level} 
                className={`flex items-start pb-6 relative ${
                  index < selectedRole.careerPath.length - 1 ? 'border-l-2 border-dashed border-muted ml-3' : ''
                }`}
              >
                <div className="absolute -left-3">
                  {isPastLevel ? (
                    <div className="w-6 h-6 rounded-full bg-web3-green text-black flex items-center justify-center">
                      <CheckCircle className="w-5 h-5" />
                    </div>
                  ) : isCurrentLevel ? (
                    <div className="w-6 h-6 rounded-full bg-primary text-black flex items-center justify-center">
                      {index + 1}
                    </div>
                  ) : (
                    <div className="w-6 h-6 rounded-full bg-muted text-white/70 flex items-center justify-center">
                      {index + 1}
                    </div>
                  )}
                </div>
                
                <div className="ml-6">
                  <h3 className={`font-medium ${isCurrentLevel ? 'text-primary' : isPastLevel ? 'text-web3-green' : 'text-white/70'}`}>
                    {path.title}
                  </h3>
                  
                  {isCurrentLevel && nextLevelPath && (
                    <div className="mt-3 space-y-3">
                      <div>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-white/70">Experience: {experience}/{expRequired} XP</span>
                          <span className="text-white/70">{Math.round(expProgress)}%</span>
                        </div>
                        <Progress value={expProgress} className="h-2" />
                      </div>
                      
                      <div>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-white/70">Years: {yearsPassed}/{yearsRequired}</span>
                          <span className="text-white/70">{Math.round(yearsProgress)}%</span>
                        </div>
                        <Progress value={yearsProgress} className="h-2" />
                      </div>
                      
                      <div className="bg-web3-blue/20 rounded p-3 mt-3">
                        <div className="flex items-start">
                          <ChevronRight className="w-4 h-4 text-primary mt-0.5 mr-1" />
                          <div>
                            <p className="text-sm text-white/80">Next promotion to <span className="text-white font-medium">{nextLevelPath.title}</span> requires:</p>
                            <ul className="mt-2 space-y-1 text-sm text-white/70 ml-4">
                              <li className="flex items-center">
                                <Award className="w-3 h-3 mr-2" /> {nextLevelPath.requiredExperience} experience points
                              </li>
                              <li className="flex items-center">
                                <Clock className="w-3 h-3 mr-2" /> {nextLevelPath.requiredYears} years in current position
                              </li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {isPastLevel && (
                    <p className="text-sm text-green-400 mt-1">
                      Completed
                    </p>
                  )}
                  
                  {isFutureLevel && (
                    <p className="text-sm text-white/60 mt-1">
                      Required: {path.requiredExperience} XP, {path.requiredYears} years
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CareerTab;
